import React, { RefObject } from 'react';
import { motion } from "framer-motion";
import { ChevronDownIcon, ChevronUpIcon, Cross2Icon, UploadIcon, PlusCircledIcon } from "@radix-ui/react-icons";
import { useDispatch, useSelector } from 'react-redux';
import { useFileInputCancelListener } from '../../hooks/useFileInputCancelListener';
import { setTargetLanguages, clearUploadedFiles, clearGlossaryForLanguage } from '../../store/chatSlice';
import { hideGlobalBlocking, showGlobalBlocking } from '../../store/loadingSlice';
import { useDeviceDetection } from "../../hooks/useDeviceDetection";
import { RootState } from "../../store/store";
import { LanguagePicker } from "./LanguageSelector";
import { useTheme } from "../Theme/ThemeProvider";
import type { ChatContentItem } from '../../utils/chat.service';
import {
  TRANSLATION_SUPPORTED_ACCEPT,
  TRANSLATION_SUPPORTED_FORMATS_LABEL,
} from '../../utils/translationMessageContent';

export interface ITranslationMessage {
  id?: string | number;
  timestamp: Date | string;
  role: string;
  content: string | ChatContentItem[];
  error?: string; // optional error field to capture translation errors
  translationData?: Array<{ // optional translation file data for download buttons
  filename: string;
  content_base64: string;
  language: string;
}>;
}

interface TranslationModeProps {
  uploadedFiles: any[];
  awaitingFileSelection: boolean;
  chatPage?: boolean;
  iconSize?: number;
  translationFileInputRef: RefObject<HTMLInputElement | null>;
  bottomBubbleContainerRef: RefObject<HTMLDivElement | null>;
  textareaRef: RefObject<HTMLTextAreaElement | null>;
  previousHeightRef: RefObject <number>;
  filesBeingProcessedRef: RefObject <boolean>;
  onHeightChange?: (height: number) => void;
  setAwaitingFileSelection: (value: boolean) => void;
  setSelectionWarning: (message: string | null) => void;
  setTranslationWarning?: (warning: { title: string; subtitle?: string; message: string } | null) => void;
  handleRemoveFile: (name: string) => void;
  handleTranslateDocuments: () => void;
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  clearChatInputDragOverlay?: () => void;
  abortTranslationFileReads?: () => void;
}

// Format file size for display (e.g. "0.96 MB")
export const formatFileSize = (sizeInBytes?: number) => {
  if (!sizeInBytes && sizeInBytes !== 0) return '';
  const size = Number(sizeInBytes) || 0;
  if (size === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(size) / Math.log(1024));
  const value = size / Math.pow(1024, i);
  return `${value.toFixed(2)} ${units[i]}`;
}; 

export const TranslationMode: React.FC<TranslationModeProps> = ({
  uploadedFiles,
  awaitingFileSelection,
  chatPage,
  iconSize,
  translationFileInputRef,
  bottomBubbleContainerRef,
  textareaRef,
  previousHeightRef,
  filesBeingProcessedRef,
  onHeightChange,
  setAwaitingFileSelection,
  setSelectionWarning,
  setTranslationWarning,
  handleRemoveFile,
  handleTranslateDocuments,
  handleFileChange,
  clearChatInputDragOverlay,
  abortTranslationFileReads,
}) => {
  const dispatch = useDispatch();
    const {lastTranslation, targetLanguages, isTranslating, glossaryFiles } = useSelector((state: RootState) => state.chat); 
    const { mode } = useTheme();
    // Device detection using custom hook
    const { isMobile, isTablet, windowHeight } = useDeviceDetection();
    const [isCollapsed, setIsCollapsed] = React.useState({ filePreview: isMobile, languageSelection: isMobile});
    const [isMobileLangMenuOpen, setIsMobileLangMenuOpen] = React.useState(false);
    const [pendingLanguages, setPendingLanguages] = React.useState<any[]>([]);
    const [showGlossaryHint, setShowGlossaryHint] = React.useState(true);
    const [isDragOverUpload, setIsDragOverUpload] = React.useState(false);

    React.useEffect(() => {
      // show the hint when languages are selected; allow user to dismiss
      if (targetLanguages && targetLanguages.length > 0) setShowGlossaryHint(true);
    }, [targetLanguages]);

    const handleTranslationFileDialogCancel = React.useCallback(() => {
      dispatch(hideGlobalBlocking());
      setAwaitingFileSelection(false);
      filesBeingProcessedRef.current = false;
    }, [dispatch, setAwaitingFileSelection, filesBeingProcessedRef]);
    useFileInputCancelListener(translationFileInputRef, handleTranslationFileDialogCancel);

    // Check if this is a duplicate (same files + languages + glossaries as last translation)
    const canTranslate = React.useMemo(() => {
      const currentFileNames = uploadedFiles.map(f => f.name).sort();
      const currentLanguageCodes = targetLanguages.map(l => l.languageCode).sort();
      const currentGlossaryIDs = (glossaryFiles || []).map(g => `${g.lang}:${g.file.filename}`).sort();

      const isDuplicate = !!(lastTranslation &&
        JSON.stringify(lastTranslation.fileNames) === JSON.stringify(currentFileNames) &&
        JSON.stringify(lastTranslation.languageCodes) === JSON.stringify(currentLanguageCodes) &&
        JSON.stringify(lastTranslation.glossaryFiles || []) === JSON.stringify(currentGlossaryIDs));
      return  uploadedFiles.length > 0 && targetLanguages.length > 0 && !isDuplicate && !isTranslating;
    }, [uploadedFiles, targetLanguages, glossaryFiles, lastTranslation, isTranslating]);

    const renderCollapse = (type: 'filePreview' | 'languageSelection', isCollapsed: boolean) => {
      // Mobile card layout
      if (isMobile) {
        const handleCardClick = (e: React.MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
          if (type === 'languageSelection') {
            if (!awaitingFileSelection) {
              setPendingLanguages(targetLanguages);
              setIsMobileLangMenuOpen(true);
            }
          } else {
            setIsCollapsed(prev => ({ ...prev, [type]: !prev[type] }));
          }
        };

        const handleUploadIconClick = (e: React.MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
          if (translationFileInputRef.current) {
            translationFileInputRef.current.value = '';
            filesBeingProcessedRef.current = false;
            dispatch(showGlobalBlocking());
            const handleFocus = () => {
              dispatch(hideGlobalBlocking());
              setAwaitingFileSelection(true);
              window.removeEventListener('focus', handleFocus);
            };
            window.addEventListener('focus', handleFocus);
            translationFileInputRef.current.click();
          }
        };

        if (type === 'languageSelection') {
          return (
            <div
              className="rounded-xl border border-[var(--color-border)] bg-[var(--color-secondary)] px-4 py-3 cursor-pointer"
              onClick={handleCardClick}
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-[var(--color-text)]">Choose Language</span>
                <ChevronDownIcon width={iconSize} className="flex-shrink-0 text-[var(--color-text)] opacity-70" />
              </div>
              <p className="text-xs text-[var(--color-text)] opacity-60 mt-1">
                {targetLanguages && targetLanguages.length > 0
                  ? `${targetLanguages.length} Language${targetLanguages.length > 1 ? 's' : ''} selected`
                  : 'Translates up to 2 Languages'}
              </p>
              {targetLanguages && targetLanguages.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {targetLanguages.map((lang: any) => (
                    <button
                      key={lang.languageCode}
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleTargetLanguageChange(targetLanguages.filter((l: any) => l.languageCode !== lang.languageCode));
                      }}
                      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-[#5E95ED] bg-[#E8F3FF] text-xs text-[#5E95ED] font-medium"
                      aria-label={`Remove ${lang.label}`}
                    >
                      <span className="truncate max-w-[140px]">{lang.label}</span>
                      <Cross2Icon width={11} height={11} className="flex-shrink-0 opacity-80" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        }

        return (
          <div
            className="rounded-xl border border-[var(--color-border)] bg-[var(--color-secondary)] px-4 py-3"
          >
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-[var(--color-text)]">
                {uploadedFiles.length > 0
                  ? `${uploadedFiles.length} File${uploadedFiles.length > 1 ? 's' : ''} selected`
                  : 'Upload up to 5 documents'}
              </span>
              <button
                type="button"
                onClick={handleUploadIconClick}
                className="text-[var(--color-text)] opacity-70 hover:opacity-100 p-1"
                aria-label="Upload file"
              >
                <UploadIcon width={18} height={18} />
              </button>
            </div>

            {/* File list shown inline when files are uploaded */}
            {uploadedFiles.length > 0 && (
              <div className="mt-2 flex flex-col gap-2">
                {uploadedFiles.map((f) => (
                  <div
                    key={f.key || f.name}
                    className="flex items-center justify-between px-3 py-2 rounded-lg"
                    style={{
                      backgroundColor: document.documentElement.getAttribute('data-theme')?.startsWith('dark-') ? '#3a3a3a' : 'var(--color-neutral-gray)',
                      color: 'white',
                      opacity: 1,
                      filter: 'none',
                      boxShadow: '0 2px 16px 0 rgba(0, 0, 0, 0.28)'
                    }}
                  >
                    <div className="flex flex-col min-w-0">
                      <span className="font-semibold truncate text-sm" style={{ maxWidth: '220px' }} title={f.name}>{f.name}</span>
                      <span className="text-xs opacity-80">{f.size ? `File Size: ${formatFileSize(f.size)}` : ''}</span>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleRemoveFile(f.name); }}
                      className="p-1 rounded-full hover:bg-white/10 flex items-center justify-center flex-shrink-0"
                      aria-label={`Remove uploaded file ${f.name}`}
                    >
                      <Cross2Icon width={20} height={20} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Subtitle only when no files */}
            {uploadedFiles.length === 0 && (
              <p className="text-xs text-[var(--color-text)] opacity-60 mt-1">
                Max. File Size: <strong>10MB</strong> | TXT, PDF, Word, Excel, PPTX, CSV, MSG
              </p>
            )}
          </div>
        );
      }

      // Desktop layout (unchanged)
      return (
        <div className="flex items-center gap-2">
          <div
            className="w-full flex justify-between items-center text-[#5E95ED]"
            aria-label={isCollapsed ? 'Expand translation details' : 'Collapse translation details'}
          >
            <span className="text-sm font-semibold-[500] text-[var(--color-text)]">
              {type === 'filePreview' ? 'Upload up to 5 documents' : 'Choose up to 2 target languages'}
            </span>
          </div>
        </div>
      );
    }

    // Centralized handler so inline and mobile pickers behave identically
    const handleTargetLanguageChange = (newSelection: any[]) => {
      dispatch(setTargetLanguages(newSelection));
      // Measure height after selection changes
      requestAnimationFrame(() => {
        if (bottomBubbleContainerRef.current && onHeightChange) {
          const baseHeight = isTablet ? 64 : 56;
          const maxContentHeight = 128;
          const BASE_PAGE_OFFSET = !chatPage ? 110 : 0;
          const GREETING_INPUT_PADDING = !chatPage ? (iconSize || 32) : 0;
          const GREETING_SPACER = !chatPage ? 36 : 0;
          const EXTRA_GALLERY_OFFSET = BASE_PAGE_OFFSET + GREETING_INPUT_PADDING + GREETING_SPACER;
          const bottomBubbleHeight = bottomBubbleContainerRef.current.offsetHeight;
          let currentFinalHeight = baseHeight;
          if (textareaRef.current) {
            const currentScrollHeight = textareaRef.current.scrollHeight;
            const currentContentHeight = Math.max(currentScrollHeight, baseHeight);
            const currentNeedsScrolling = currentContentHeight > maxContentHeight;
            currentFinalHeight = currentNeedsScrolling ? maxContentHeight : currentContentHeight;
          }
          const totalComponentHeight = currentFinalHeight + bottomBubbleHeight + EXTRA_GALLERY_OFFSET;
          if (Math.abs(totalComponentHeight - previousHeightRef.current) > 5) {
            previousHeightRef.current = totalComponentHeight;
            onHeightChange(totalComponentHeight);
          }
        }
      });
    };

  return (
    <motion.div
      initial={{ opacity: 0, scaleY: 0 }}
      animate={{ opacity: 1, scaleY: 1 }}
      exit={{ opacity: 0, scaleY: 0 }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
      style={{ transformOrigin: 'bottom', maxHeight: windowHeight ? windowHeight - 100 : windowHeight, overflowY: 'auto', paddingBottom: isMobile ? 8 : 0 }}
    >
      {/* Horizontal 3-Column Layout on desktop, Vertical on mobile */}
      <div className={`flex ${isMobile ? 'flex-col gap-2' : 'flex-row gap-4 justify-center'}`}>
        {/* Step 1: Upload Documents */}
        <motion.div 
          className={`flex flex-col gap-2 flex-1 ${isMobile ? '' : 'p-2'}`}
          layout
          initial={uploadedFiles.length === 0 ? { opacity: 0, scale: 0.95 } : false}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          style={{ maxWidth: isMobile ? '100%' : '45%', borderRadius: 8, border: isDragOverUpload ? '3px dashed #5E95ED' : '2px solid transparent', backgroundColor: isDragOverUpload ? 'rgba(94, 149, 237, 0.1)' : undefined, transition: 'border 0.15s, background-color 0.15s' }}
          onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragOverUpload(true); }}
          onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragOverUpload(true); }}
          onDragLeave={(e) => { e.preventDefault(); e.stopPropagation();
            // only clear if leaving the column entirely (not entering a child)
            if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsDragOverUpload(false);
          }}
          onDrop={(e) => {
            e.preventDefault(); e.stopPropagation(); setIsDragOverUpload(false);
            clearChatInputDragOverlay?.(); // clear ChatInput's isDraggingOver overlay (stopPropagation prevents ChatInput's handleDrop from clearing it)
            const files = e.dataTransfer?.files;
            if (files && files.length > 0 && translationFileInputRef.current) {
              setAwaitingFileSelection(true);
              filesBeingProcessedRef.current = true;
              const dataTransfer = new DataTransfer();
              Array.from(files).forEach(file => dataTransfer.items.add(file));
              translationFileInputRef.current.files = dataTransfer.files;
              const event = new Event('change', { bubbles: true });
              translationFileInputRef.current.dispatchEvent(event);
            }
          }}
        >
          {renderCollapse('filePreview', isCollapsed.filePreview)}
          {!isCollapsed.filePreview && (
            <div className="h-full flex flex-col gap-3 justify-center items-center">
              { uploadedFiles.length === 0 && !awaitingFileSelection && (
                <div
                  onClick={() => {
                    if (translationFileInputRef.current) {
                      translationFileInputRef.current.value = '';
                      filesBeingProcessedRef.current = false;
                      dispatch(showGlobalBlocking());

                      const handleFocus = () => {
                        dispatch(hideGlobalBlocking());
                        setAwaitingFileSelection(true);
                        window.removeEventListener('focus', handleFocus);
                      };
                      window.addEventListener('focus', handleFocus);

                      translationFileInputRef.current.click();
                    }
                  }}
                  onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); e.currentTarget.style.backgroundColor = 'var(--hover)'; }}
                  onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); e.currentTarget.style.backgroundColor = ''; }}
                  onDrop={(e) => {
                    e.preventDefault(); e.stopPropagation(); e.currentTarget.style.backgroundColor = '';
                    setIsDragOverUpload(false); // stopPropagation prevents outer column's onDrop from clearing this
                    clearChatInputDragOverlay?.(); // clear ChatInput's isDraggingOver overlay
                    const files = e.dataTransfer?.files;
                    if (files && files.length > 0 && translationFileInputRef.current) {
                      setAwaitingFileSelection(true);
                      filesBeingProcessedRef.current = true;
                      const dataTransfer = new DataTransfer();
                      Array.from(files).forEach(file => dataTransfer.items.add(file));
                      translationFileInputRef.current.files = dataTransfer.files;
                      const event = new Event('change', { bubbles: true });
                      translationFileInputRef.current.dispatchEvent(event);
                    }
                  }}
                  className="flex flex-col items-center gap-1 px-6 py-2 border-2 border-dashed border-[var(--color-accent)] rounded-lg cursor-pointer hover:bg-[var(--hover)] transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <UploadIcon width={16} height={16} className="text-[var(--color-text)]" />
                    <span className="text-sm font-medium text-[var(--color-text)]">Click to upload files</span>
                  </div>
                  <span className="text-xs" style={{ color: 'var(--color-text)', opacity: 0.6 }}>or drag files here</span>
                </div>
              )}

              {uploadedFiles.length === 0 && awaitingFileSelection && (
                <div
                  onMouseDown={() => {
                    // Use onMouseDown: file read can complete between mousedown→mouseup,
                    // causing React to unmount this div before click fires.
                    // Also clear uploadedFiles: the file may have already been dispatched
                    // to Redux inside a FileReader onload macrotask before React re-rendered
                    // and while this cancel button was still visible.
                    // Abort any in-progress FileReader instances so large files stop reading immediately.
                    abortTranslationFileReads?.();
                    dispatch(hideGlobalBlocking());
                    dispatch(clearUploadedFiles());
                    setTranslationWarning?.(null);
                    setAwaitingFileSelection(false);
                    filesBeingProcessedRef.current = false;
                  }}
                  className="flex flex-col items-center gap-1 px-6 py-3 border-2 border-dashed border-[var(--color-accent)] rounded-lg cursor-pointer hover:bg-[var(--hover)] transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-[var(--color-accent)] border-t-transparent rounded-full animate-spin"></div>
                    <span className="text-sm font-medium text-[var(--color-text)]">Waiting for file<span className="animate-pulse">...</span></span>
                  </div>
                  <span className="text-xs" style={{ color: 'var(--color-text)', opacity: 0.6, cursor: 'pointer' }}>Click to cancel</span>
                </div>
              )}

              {uploadedFiles.length === 0 && !awaitingFileSelection && (
                <div className="flex flex-col items-center gap-1 px-6 py-3 text-xs text-center text-[var(--color-text)] opacity-50">
                  <span>Input: 5 files, 10MB / 24h quota = 10 files</span>
                  <span>Supported formats: {TRANSLATION_SUPPORTED_FORMATS_LABEL}</span>
                </div>
              )}

              {/* File Previews - stacked vertically */}
              {uploadedFiles.length > 0 && uploadedFiles.map((f, idx) => (
                <div
                  key={f.key || f.name}
                  className="flex items-center justify-between w-full px-4 py-2 rounded-md shadow-sm"
                  style={{
                    backgroundColor: mode === 'dark' ? '#3a3a3a' : 'var(--color-neutral-gray)',
                    color: 'white'
                  }}
                  aria-label={`File ${f.name}`}
                >
                  <div className="flex flex-col min-w-0">
                    <span className="font-semibold truncate text-sm" style={{ maxWidth: isMobile ? '220px' : '320px' }} title={f.name}>{f.name}</span>
                    <span className="text-xs" style={{ opacity: 0.8 }}>{f.size ? `File Size: ${formatFileSize(f.size)}` : ''}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveFile(f.name)}
                    className="p-1 rounded-full hover:bg-white/10 flex items-center justify-center"
                    aria-label={`Remove uploaded file ${f.name}`}
                  >
                    <Cross2Icon width={24} height={24} />
                  </button>
                </div>
              ))}

              {/* Upload button below list (shows when less than 5 uploaded) */}
              {uploadedFiles.length > 0 && uploadedFiles.length < 5 && (
                <div className="flex justify-center pt-2 text-[#5E95ED]">
                  <button
                    type="button"
                    onClick={() => {
                      if (translationFileInputRef.current) {
                        translationFileInputRef.current.value = '';
                        filesBeingProcessedRef.current = false;
                        dispatch(showGlobalBlocking());
                        const handleFocus = () => {
                          dispatch(hideGlobalBlocking());
                          setAwaitingFileSelection(true);
                          window.removeEventListener('focus', handleFocus);
                        };
                        window.addEventListener('focus', handleFocus);
                        translationFileInputRef.current.click();
                      }
                    }}
                    className="inline-flex justify-center gap-2 px-4 py-2 rounded text-sm font-medium border border-transparent color-[#5E95ED] hover:bg-[var(--hover)] transition-colors"
                    aria-label="Upload another file"
                  >
                    <UploadIcon width={18} height={18} />
                    <span>Upload File</span>
                  </button>
                </div>
              )}

              {/* Glossary hint shown when a target language is selected */}
              {targetLanguages && targetLanguages.length > 0 && showGlossaryHint && (() => {
                const glossaryCount = targetLanguages.reduce((acc, l) => acc + (Array.isArray(glossaryFiles) ? glossaryFiles.filter(g => g.lang === l.languageCode).length : 0), 0);
                const langsWithFiles = targetLanguages.filter(l => Array.isArray(glossaryFiles) && glossaryFiles.some(g => g.lang === l.languageCode)).map(l => l.label);
                const formatLangs = (arr: string[]) => {
                  if (arr.length === 0) return '';
                  if (arr.length === 1) return arr[0];
                  if (arr.length === 2) return `${arr[0]} & ${arr[1]}`;
                  return `${arr.slice(0, -1).join(', ')} & ${arr[arr.length - 1]}`;
                };

                if (glossaryCount > 0) {
                  return (
                    <div className="w-full px-3 py-2 rounded-md bg-[#eef6ff] border-l-4 border-[#3b82f6] flex items-center gap-3">
                      <div className="flex-1">
                        <div className="text-sm font-semibold text-black">{`${glossaryCount} Glossary file${glossaryCount > 1 ? 's' : ''} added!`}</div>
                        <div className="text-xs text-black opacity-90">{`Language: ${formatLangs(langsWithFiles)}`}</div>
                      </div>
                      <button
                        type="button"
                        aria-label="Remove glossary files"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          // Remove the glossary files for the currently selected languages
                          targetLanguages.forEach(l => {
                            dispatch(clearGlossaryForLanguage(l.languageCode));
                          });
                          // Note: We don't need to set showGlossaryHint(false) because removing the files
                          // causes glossaryCount to become 0, which transitions the UI back to the 
                          // default "Add a Glossary file" hint automatically.
                        }}
                        className="p-1 rounded-full hover:bg-gray-100 flex items-center justify-center"
                      >
                        <Cross2Icon width={20} height={20} />
                      </button>
                    </div>
                  );
                }

                // default hint when no glossary uploaded yet
                return (
                  <div className="w-full px-3 py-2 rounded-md bg-yellow-50 border-l-4 border-yellow-300 flex items-center gap-3">
                    <div className="flex-1">
                      <div className="text-xs font-medium font-semibold text-black">
                        Add a Glossary file by clicking the <PlusCircledIcon width={16} height={16} color="#5E95ED" style={{ display: 'inline', verticalAlign: 'middle', transform: 'translateY(-2px)' }} /> next to a language (limit 10MB)
                      </div>
                      <div className="text-xs opacity-80">
                        <span className="text-black">Supported Formats: CSV, TSV | </span>
                        <span className="text-[#0000FF] cursor-pointer hover:underline" onClick={() => window.open('https://skydrive3m-my.sharepoint.com/:w:/g/personal/aasn6zz_mmm_com/IQCjavUkh3HVSaxlOtozWQRGAaWLaQYkpK833eSKhO8vDb8?e=rO9AfG', '_blank')}>Click for Guide to Glossary</span>
                        </div>
                    </div>
                    <button
                      type="button"
                      aria-label="Dismiss glossary hint"
                      onClick={() => setShowGlossaryHint(false)}
                      className="p-1 rounded-full hover:bg-yellow-100 flex items-center justify-center"
                    >
                      <Cross2Icon width={20} height={20} />
                    </button>
                  </div>
                );
              })()}
            </div>
          )}
        </motion.div>

        <div className={`flex flex-col gap-2 ${isMobile ? 'w-full' : 'p-2 w-[45%]'}`}>
          {/* Step 2: Choose Languages */}
          <motion.div 
            className="flex flex-col gap-2 flex-1 w-inherit"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
          >
            {renderCollapse('languageSelection', isCollapsed.languageSelection)}

            {!isCollapsed.languageSelection && (
              <div
                className={awaitingFileSelection ? 'pointer-events-none opacity-50 select-none' : ''}
                title={awaitingFileSelection ? 'Please wait for the file to finish uploading' : undefined}
                aria-disabled={awaitingFileSelection || undefined}
              >
                <LanguagePicker
                  value={targetLanguages}
                  onChange={handleTargetLanguageChange}
                  onSelectionLimitExceeded={(message) => setTranslationWarning?.({ 
                    title: 'Limit Reached', 
                    subtitle: 'Language selection limit exceeded.',
                    message 
                  })}
                  onGlossaryFileTooLarge={(title, message) => setTranslationWarning?.({ title, message })}
                  onMultipleGlossaryFiles={(warning) => setTranslationWarning?.(warning)}
                  chatPage={chatPage}
                  maxSelections={2}
                />
              </div>
              )
            }
          </motion.div>

          {/* Mobile: Language selection full-screen menu */}
          {isMobile && isMobileLangMenuOpen && (
            <div className="fixed inset-0 z-50 flex items-end bg-black/40" onClick={() => setIsMobileLangMenuOpen(false)}>
              {/* Backdrop */}
              <div className="absolute inset-0 bg-black bg-opacity-50" />

              <motion.div
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                exit={{ y: '100%' }}
                transition={{ type: 'spring', damping: 30, stiffness: 300 }}
                className="relative w-full bg-[var(--color-secondary)] rounded-t-2xl shadow-2xl flex flex-col"
                style={{ maxHeight: '85vh' }}
                onClick={(e) => e.stopPropagation()}>
                  {/* Header */}
                  <div className="flex items-center px-4 py-4 flex-shrink-0">
                    <span className="text-base font-semibold text-[var(--color-text)]">Select Language</span>
                    <button
                      type="button"
                      aria-label="Close language selector"
                      onClick={() => setIsMobileLangMenuOpen(false)}
                      className="ml-auto inline-flex items-center justify-center w-8 h-8 text-[var(--color-text)]"
                    >
                      <Cross2Icon width={18} height={18} />
                    </button>
                  </div>

                  {/* Scrollable language list */}
                  <div className="flex-1 overflow-y-auto px-3 pb-2">
                    <LanguagePicker
                      value={pendingLanguages}
                      onChange={setPendingLanguages}
                      onSelectionLimitExceeded={(message) => setTranslationWarning?.({
                        title: 'Limit Reached',
                        subtitle: 'Language selection limit exceeded.',
                        message
                      })}
                      onGlossaryFileTooLarge={(title, message) => setTranslationWarning?.({ title, message })}
                      onMultipleGlossaryFiles={(warning) => setTranslationWarning?.(warning)}
                      chatPage={chatPage}
                      maxSelections={2}
                    />
                  </div>

                  {/* Submit button */}
                  <div className="flex-shrink-0 px-4 pb-6 pt-3">
                    <button
                      type="button"
                      onClick={() => {
                        handleTargetLanguageChange(pendingLanguages);
                        setIsMobileLangMenuOpen(false);
                      }}
                      className="w-full py-3 rounded-xl text-sm font-semibold border border-[#5E95ED] text-[#5E95ED] bg-transparent hover:bg-[#5E95ED]/10 transition-colors"
                    >
                      Submit
                    </button>
                  </div>
              </motion.div>
            </div>
          )}
      
          {/* Step 3: Translate - only show when languages are selected */}
          <motion.div 
            className="flex flex-col gap-2 flex-1 w-inherit"
            layout
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
          >
            <button
              type="button"
              onClick={handleTranslateDocuments}
              disabled={!canTranslate}
              className={isMobile
                  ? `w-full flex justify-center gap-2 px-4 py-2 rounded-lg text-sm transition-all border border-[#3473DE] bg-[#3473DE] hover:bg-[#2a5fbf] text-white disabled:cursor-not-allowed disabled:bg-gray-400 disabled:border-gray-500`
                  : `inline-flex justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all border border-[#5E95ED] bg-[#5E95ED] hover:bg-[#2a5fbf] hover:border-[#2a5fbf] text-white disabled:cursor-not-allowed disabled:bg-gray-400 disabled:border-gray-500`}
              style={{ 
                opacity: !canTranslate ? 0.6 : 1,
                cursor: !canTranslate ? 'not-allowed' : 'pointer',
              }}
              aria-label="Translate"
            >
              {isTranslating ? (
                <>
                  <div 
                    className="animate-spin"
                    style={{
                      width: 16,
                      height: 16,
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      borderTop: '2px solid white',
                      borderRadius: '50%'
                    }}
                  />
                  <span>Translating...</span>
                </>
              ) : (
                <span>Translate</span>
              )}
            </button>
          </motion.div>
        </div>
      </div>
      


      {/* Hidden file input for translation mode */}
      <input
        ref={translationFileInputRef}
        type="file"
        multiple
        accept={TRANSLATION_SUPPORTED_ACCEPT}
        style={{
          position: 'absolute',
          left: '-9999px',
          top: 0,
          width: '1px',
          height: '1px',
          opacity: 0.01,
        }}
        onChange={handleFileChange}
      />
    </motion.div>
  );
};
